/**
 * Command registry for the Cmd+K palette.
 *
 * Any part of the codebase can register commands that appear in the palette.
 * Commands are registered with `register()` which returns an unregister function
 * (designed for React useEffect cleanup). The palette consumes commands via
 * `useCommands()` which re-renders when the registry changes.
 *
 * Usage:
 *   // In a component (registers while mounted, auto-unregisters on unmount)
 *   useEffect(() => {
 *     return commandRegistry.register({
 *       id: 'patient-add-note',
 *       label: 'Add note for Sarah Chen',
 *       icon: 'file-text',
 *       keywords: ['note', 'add'],
 *       action: () => navigate('/'),
 *     })
 *   }, [patient.uuid])
 *
 *   // In AppShell (registers base navigation commands once)
 *   useEffect(() => {
 *     const unregister = commandRegistry.registerMany(baseCommands)
 *     return unregister
 *   }, [])
 */

import { useSyncExternalStore } from 'react'
import type { LucideIcon } from 'lucide-react'

export interface Command {
  /** Unique identifier */
  id: string
  /** Display label */
  label: string
  /** Optional subtitle */
  subtitle?: string
  /** Lucide icon component */
  icon: LucideIcon
  /** Searchable keywords (matched by fuzzy filter) */
  keywords?: string[]
  /** Keyboard shortcut hint (e.g. "g n") */
  shortcut?: string
  /** Permission required (checked via hasPermission) */
  permission?: string
  /** Navigation path (for commands that navigate) */
  path?: string
  /** Action to execute on selection */
  action: () => void
  /** Command category for grouping */
  category?: 'action' | 'navigation' | 'contextual'
  /** Sort priority within category (higher = earlier). Default 0. */
  priority?: number
  /** Whether to show in the default (empty query) state */
  showInDefault?: boolean
}

type Listener = () => void

class CommandRegistry {
  private commands = new Map<string, Command>()
  private listeners = new Set<Listener>()
  private version = 0

  /**
   * Register a command. Returns an unregister function (for useEffect cleanup).
   */
  register(command: Command): () => void {
    this.commands.set(command.id, command)
    this.version++
    this.notify()
    return () => {
      this.commands.delete(command.id)
      this.version++
      this.notify()
    }
  }

  /**
   * Register multiple commands at once. Returns a single unregister function.
   */
  registerMany(commands: Command[]): () => void {
    for (const cmd of commands) {
      this.commands.set(cmd.id, cmd)
    }
    this.version++
    this.notify()
    return () => {
      for (const cmd of commands) {
        this.commands.delete(cmd.id)
      }
      this.version++
      this.notify()
    }
  }

  /**
   * Get all registered commands, optionally filtered by permission.
   */
  getCommands(hasPermission?: (p: string) => boolean): Command[] {
    const commands = Array.from(this.commands.values())
    const filtered = hasPermission
      ? commands.filter((cmd) => !cmd.permission || hasPermission(cmd.permission))
      : commands
    return filtered.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
  }

  /**
   * Subscribe to changes (for useSyncExternalStore).
   */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /**
   * Get current snapshot version (for useSyncExternalStore).
   */
  getSnapshot(): number {
    return this.version
  }

  private notify() {
    this.listeners.forEach((fn) => fn())
  }
}

export const commandRegistry = new CommandRegistry()

/**
 * React hook to consume registered commands. Re-renders when commands change.
 */
export function useCommands(hasPermission?: (p: string) => boolean): Command[] {
  useSyncExternalStore(
    (fn) => commandRegistry.subscribe(fn),
    () => commandRegistry.getSnapshot()
  )
  return commandRegistry.getCommands(hasPermission)
}
